import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Logo } from '@/components/shared/Logo';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft, ArrowRight, Save, ShieldCheck, CheckCircle2, Sparkles, Home,
  Loader2, AlertCircle, Eye, HourglassIcon, Clock, WifiOff,
} from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import LanguageSwitcher from '@/components/layout/LanguageSwitcher';
import { AssignmentConsentGate } from '@/components/enduser/AssignmentConsentGate';
import { LocalizedDateInput } from '@/components/shared/LocalizedDateInput';
import { RecruitmentContextPanel } from '@/components/recruitment/RecruitmentContextPanel';
import { CompetencyContextPanel } from '@/components/recruitment/CompetencyContextPanel';
import ClimaraIntroGate from '@/components/enduser/ClimaraIntroGate';
import AssessmentIntroGate from '@/components/enduser/AssessmentIntroGate';

const ASSESSMENT_DONE_STATES = ['submitted'];

export default function EndUserAssessment() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Rendered inside the EvaluatedLayout (sticky h-16 top bar) on /me/* routes,
  // standalone on /assessment/:id — offset our sticky header accordingly.
  const underLayout = useLocation().pathname.startsWith('/me/');
  const { t, i18n } = useTranslation();
  const isEN = (i18n.language || '').toLowerCase().startsWith('en');
  // Helper: prefer the localized text if available
  const tx = (es, en) => (isEN && en ? en : (es || en || ''));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [version, setVersion] = useState(null);
  const [answers, setAnswers] = useState({});  // question_id -> option_id (string for single, array for multi)
  const [idx, setIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reportState, setReportState] = useState(null); // {status, version?}
  const [questionnaire, setQuestionnaire] = useState(null);
  const [needsAssignmentConsent, setNeedsAssignmentConsent] = useState(false);
  // Sprint CMA-C — Climara intro gate. Shown once before the first question
  // when the assignment is part of the Climara suite AND no autosave/responses
  // exist yet (i.e. the candidate hasn't started). Pure UX wrapper.
  const [climaraIntroDismissed, setClimaraIntroDismissed] = useState(false);
  // R2 — generic intro gate (all non-Climara suites), with resume support.
  const [introDismissed, setIntroDismissed] = useState(false);
  const autosaveTimer = useRef(null);
  const finalizingRef = useRef(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/me/assignments/${id}`);
        if (!alive) return;
        setAssignment(data.assignment);
        setVersion(data.version);
        setQuestionnaire(data.questionnaire || null);
        // Sprint A — per-assignment consent gate.
        setNeedsAssignmentConsent(!data.assignment?.consent?.accepted_at);
        // load any existing answers
        const existing = data.response?.answers || {};
        const seed = {};
        Object.entries(existing).forEach(([qid, a]) => {
          // ResponseAnswer dict
          if (a && typeof a === 'object' && Array.isArray(a.option_ids)) {
            if (a.option_ids.length === 1) seed[qid] = a.option_ids[0];
            else if (a.option_ids.length > 1) seed[qid] = a.option_ids;
            else if (a.text) seed[qid] = a.text;
          }
        });
        setAnswers(seed);

        if (ASSESSMENT_DONE_STATES.includes(data.assignment.status)) {
          // fetch report state (may be under_review or released)
          const { data: r } = await api.get(`/me/assignments/${id}/report`);
          if (alive) setReportState(r);
        }
      } catch (e) {
        // BUG 20 — Hard gate. If consent isn't accepted, the backend returns
        // 403 {code: consent_required}; bounce the user to the consent page.
        const detail = e?.response?.data?.detail;
        const code = (detail && typeof detail === 'object') ? detail.code : null;
        if (e?.response?.status === 403 && code === 'consent_required') {
          if (alive) navigate('/me/consent', { replace: true });
          return;
        }
        // UAT Sprint 03 — Draft / unconfigured assignments now return a
        // graceful 409 instead of a raw 500. Surface a friendly "being
        // prepared" placeholder so the candidate knows to wait for their
        // consultant.
        if (e?.response?.status === 409) {
          if (alive) {
            setError('__NOT_CONFIGURED__');
          }
          return;
        }
        if (alive) setError(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id]);

  const questions = version?.questions || [];
  const q = questions[idx];
  const total = questions.length;
  // Founder UAT — array-aware answered check (multi_choice stores arrays).
  const isAnswered = (qid) => {
    const v = answers[qid];
    if (v === undefined || v === null) return false;
    if (Array.isArray(v)) return v.length > 0;
    return String(v).trim() !== '';
  };
  const answeredCount = questions.filter((qq) => isAnswered(qq.id)).length;
  const progress = useMemo(
    () => (total ? Math.round((answeredCount / total) * 100) : 0),
    [answeredCount, total]
  );

  // Autosave — CTH V6.0 PWA: explicit saved/error states, retry on reconnect,
  // flush when the app is minimized or the screen locks. Server confirmation
  // is the only source of truth: we never claim "saved" without a 2xx.
  // CTH V6.0 — only the LATEST autosave result may drive the indicator
  // (an older in-flight success must not mask a newer failure).
  const saveSeqRef = useRef(0);
  useEffect(() => {
    if (!assignment || reportState || finalizingRef.current) return;
    if (Object.keys(answers).length === 0) return;
    autosaveTimer.current = setTimeout(async () => {
      if (finalizingRef.current) return;
      const seq = ++saveSeqRef.current;
      try {
        setSaving(true);
        await api.post(`/me/assignments/${id}/answers`, { answers, submit: false });
        if (seq === saveSeqRef.current) setSaveError(false);
      } catch {
        if (seq === saveSeqRef.current) setSaveError(true);
      } finally {
        if (seq === saveSeqRef.current) setSaving(false);
      }
    }, 500);
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers]);

  // Retry pending answers when connectivity returns; flush on hide/lock.
  const answersRef = useRef(answers);
  answersRef.current = answers;
  useEffect(() => {
    const flush = async () => {
      if (finalizingRef.current || reportState) return;
      const a = answersRef.current;
      if (Object.keys(a).length === 0) return;
      const seq = ++saveSeqRef.current;
      try {
        await api.post(`/me/assignments/${id}/answers`, { answers: a, submit: false });
        if (seq === saveSeqRef.current) setSaveError(false);
      } catch {
        if (seq === saveSeqRef.current) setSaveError(true);
      }
    };
    const onOnline = () => flush();
    const onHide = () => { if (document.visibilityState === 'hidden') flush(); };
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onHide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, reportState]);

  const select = (val) => setAnswers((p) => ({ ...p, [q.id]: val }));
  const toggleMulti = (optId) => setAnswers((p) => {
    const cur = Array.isArray(p[q.id]) ? p[q.id] : [];
    return { ...p, [q.id]: cur.includes(optId) ? cur.filter((x) => x !== optId) : [...cur, optId] };
  });
  const next = () => idx < total - 1 && setIdx(idx + 1);
  const prev = () => idx > 0 && setIdx(idx - 1);

  // Founder UAT — explicit Save & Exit (flushes the pending autosave first).
  const saveAndExit = async () => {
    if (autosaveTimer.current) { clearTimeout(autosaveTimer.current); autosaveTimer.current = null; }
    try {
      setSaving(true);
      if (Object.keys(answers).length > 0) {
        await api.post(`/me/assignments/${id}/answers`, { answers, submit: false });
      }
      toast.success(t('pages.endUserAssessment.progressSaved', { defaultValue: 'Progreso guardado. Puedes continuar más tarde.' }));
    } catch {
      // silent — autosave already covers persistence
    } finally { setSaving(false); }
    navigate('/me');
  };

  const submit = async () => {
    if (questions.some((qq) => !isAnswered(qq.id))) {
      toast.error(t('toasts.pleaseAnswerAll'));
      return;
    }
    finalizingRef.current = true;
    if (autosaveTimer.current) { clearTimeout(autosaveTimer.current); autosaveTimer.current = null; }
    setSubmitting(true);
    try {
      await api.post(`/me/assignments/${id}/answers`, { answers, submit: true });
      toast.success(t('toasts.evaluationSubmitted'), { description: t('toasts.awaitingHmReview') });
      // Iter 41 — Step 5 of bare flow: route to the thank-you screen.
      navigate(`/me/assignments/${id}/done`, { replace: true });
    } catch (e) {
      finalizingRef.current = false;
      toast.error(t('toasts.submissionFailed'), { description: formatApiError(e) });
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- renders ----------

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-subtle">
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /> {t('pages.endUserAssessment.loading')}</div>
      </div>
    );
  }

  if (error) {
    const notConfigured = error === '__NOT_CONFIGURED__';
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-subtle p-6">
        <Card className="max-w-md w-full p-8 text-center shadow-elegant" data-testid={notConfigured ? 'assignment-not-configured' : 'assignment-error'}>
          <div className={`h-12 w-12 rounded-full ${notConfigured ? 'bg-info/10 text-info' : 'bg-destructive/10 text-destructive'} flex items-center justify-center mx-auto`}>
            {notConfigured ? <Clock className="h-6 w-6" /> : <AlertCircle className="h-6 w-6" />}
          </div>
          <h1 className="font-display text-xl font-bold tracking-tight mt-4">
            {notConfigured
              ? t('pages.endUserAssessment.notConfiguredTitle', { defaultValue: 'This evaluation is being prepared' })
              : t('pages.endUserAssessment.cantLoad')}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            {notConfigured
              ? t('pages.endUserAssessment.notConfiguredBody', { defaultValue: "This evaluation is being prepared by your consultant. You'll be notified when it's ready." })
              : error}
          </p>
          <Button onClick={() => navigate('/me')} className="mt-6 bg-gradient-primary hover:opacity-90">{t('pages.endUserAssessment.back')}</Button>
        </Card>
      </div>
    );
  }

  // Released-report rendering for candidates is intentionally REMOVED.
  // (Per Feb-2026 policy: candidates have no report access — the asesor
  // prepares the report, and Client Admin / HR receives it.)
  // Any non-null reportState now collapses to the neutral submission
  // acknowledgement below.
  if (reportState && (reportState.status === 'submitted_no_report'
      || reportState.status === 'under_review'
      || reportState.status === 'pending'
      || reportState.status === 'released')) {
    const climaraAck = assignment?.suite_code === 'climara';
    const exitAck = assignment?.suite_code === 'talentexit';
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-subtle p-6">
        <Card className="max-w-md w-full p-10 text-center shadow-elegant" data-testid="submission-ack">
          <div className="h-16 w-16 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto">
            <HourglassIcon className="h-8 w-8" />
          </div>
          <h1 className="font-display text-2xl font-bold tracking-tight mt-6">
            {climaraAck
              ? t('climara.thanks.title', { defaultValue: 'Thank you for participating.' })
              : exitAck
                ? t('talentexit.thanks.title', { defaultValue: 'Thank you — your exit interview has been recorded.' })
                : t('pages.endUserAssessment.thankYou')}
          </h1>
          <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
            {climaraAck
              ? t('climara.thanks.body', {
                  defaultValue:
                    'Your responses are anonymous and only consolidated organizational results will be reported.',
                })
              : exitAck
                ? t('talentexit.thanks.body', {
                    defaultValue:
                      'Your responses are confidential and will be handled by authorized personnel only. We appreciate your time and wish you the best.',
                  })
                : t('pages.endUserAssessment.submittedNoReport')}
          </p>
          {!climaraAck && !exitAck && (
            <Badge className="mt-6 bg-primary/10 text-primary border-primary/20">
              <Clock className="h-3 w-3 mr-1.5" /> {t('pages.endUserAssessment.pendingReview')}
            </Badge>
          )}
          <div className="mt-6">
            <Button onClick={() => navigate('/me')} className="bg-gradient-primary hover:opacity-90 gap-1.5">
              <Home className="h-4 w-4" /> {t('pages.endUserAssessment.back')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (!q) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-subtle">
        <p className="text-sm text-muted-foreground">{t('pages.endUserAssessment.noQuestions')}</p>
      </div>
    );
  }

  // Sprint A — Per-assignment consent gate (Ley 81). Renders full-screen
  // until the 5 mandatory checkboxes are accepted and persisted.
  if (needsAssignmentConsent && assignment) {
    return (
      <div>
        {assignment.tp_context && (
          <div className="mx-auto max-w-3xl mt-4 px-4">
            <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm" data-testid="tp-subject-banner-consent">
              <span className="font-semibold text-primary">
                {i18n.language?.startsWith('es') ? 'Evaluando a' : 'Evaluating'}: {assignment.tp_context.subject_name}
              </span>
              <span className="text-muted-foreground">
                {assignment.tp_context.subject_position ? ` · ${assignment.tp_context.subject_position}` : ''}
                {assignment.tp_context.subject_department ? ` · ${assignment.tp_context.subject_department}` : ''}
                {' · '}
                {i18n.language?.startsWith('es')
                  ? (assignment.tp_context.evaluator_role === 'supervisor' ? 'Rol: Supervisor/a' : 'Rol: Cliente Interno')
                  : (assignment.tp_context.evaluator_role === 'supervisor' ? 'Role: Supervisor' : 'Role: Internal Client')}
              </span>
            </div>
          </div>
        )}
        <AssignmentConsentGate
          assignmentId={assignment.id}
          onAccepted={() => setNeedsAssignmentConsent(false)}
        />
      </div>
    );
  }

  // Sprint CMA-C — Climara confidentiality gate. Shown once before the first
  // question when no answer has been saved yet. Uses the existing assignment
  // payload only; no extra API call.
  const isClimara = assignment?.suite_code === 'climara';
  const hasStarted = Object.keys(answers).length > 0;
  if (isClimara && !climaraIntroDismissed && !hasStarted) {
    return (
      <ClimaraIntroGate
        assignment={assignment}
        questionnaire={questionnaire}
        companyName={assignment?.company_name || null}
        estimatedMinutes={Math.max(5, Math.round((questions.length || 0) * 0.4))}
        onStart={() => setClimaraIntroDismissed(true)}
      />
    );
  }

  // R2 — generic assessment intro (name, objective, duration, question count,
  // instructions, recommendations, start/resume). Reuses Studio metadata.
  if (!isClimara && !introDismissed) {
    return (
      <AssessmentIntroGate
        assignment={assignment}
        questionnaire={questionnaire}
        companyName={assignment?.company_name || null}
        questionCount={total}
        estimatedMinutes={questionnaire?.estimated_minutes || Math.max(5, Math.round((total || 0) * 0.4))}
        answeredCount={answeredCount}
        onStart={() => {
          const firstUnanswered = questions.findIndex((qq) => answers[qq.id] === undefined);
          setIdx(firstUnanswered >= 0 ? firstUnanswered : 0);
          setIntroDismissed(true);
        }}
      />
    );
  }

  const isSelected = (optId) => {
    const v = answers[q.id];
    return Array.isArray(v) ? v.includes(optId) : v === optId;
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-subtle">
      <header className={`sticky z-20 px-4 sm:px-6 py-3 sm:py-4 border-b border-border bg-background/70 backdrop-blur-xl ${underLayout ? 'top-16' : 'top-0'}`}>
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-2 sm:gap-4">
          <div className="hidden sm:block"><Logo size="sm" /></div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground min-w-0">
            {saving
              ? <><Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" /> <span className="hidden sm:inline">{t('common.savingDots')}</span></>
              : saveError
                ? <span className="inline-flex items-center gap-1.5 text-amber-600 min-w-0" data-testid="autosave-error-indicator">
                    <WifiOff className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{t('pages.endUserAssessment.saveRetry', { defaultValue: 'Sin conexión — reintentando guardar' })}</span>
                  </span>
                : <><Save className="h-3.5 w-3.5 text-success shrink-0" /> <span className="hidden sm:inline">{t('pages.endUserAssessment.autoSaved')}</span></>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!underLayout && <LanguageSwitcher compact />}
            <Button data-testid="save-exit-button" variant="outline" size="sm" onClick={saveAndExit} disabled={saving} className="gap-1.5">
              <Save className="h-4 w-4" /> {t('pages.endUserAssessment.saveExit', { defaultValue: 'Guardar y salir' })}
            </Button>
            <Button data-testid="exit-assessment-button" variant="ghost" size="sm" onClick={() => navigate('/me')} className="gap-1.5"><ArrowLeft className="h-4 w-4" /> {t('pages.endUserAssessment.exit')}</Button>
          </div>
        </div>
      </header>

      <main className="flex-1 p-6 lg:p-10">
        <div className="max-w-3xl mx-auto">
          {/* Sprint TC-B.1 — recruitment context wrapper. Renders nothing when
              the assignment isn't linked to a TalentCheck recruitment process,
              so legacy/non-recruitment flows stay untouched. */}
          <RecruitmentContextPanel assignmentId={id} />
          {/* Sprint TCO-C — TalentCompetency context wrapper. Same philosophy:
              renders nothing when the assignment isn't a TC engagement. */}
          <CompetencyContextPanel assignmentId={id} />
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <Badge variant="outline" className="font-medium">{t('pages.endUserAssessment.questionOf', { defaultValue: 'Question {{i}} of {{total}}', i: idx + 1, total })}</Badge>
                <span className="ml-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {tx(version?.sections?.find(s => s.id === q.section_id)?.name,
                      version?.sections?.find(s => s.id === q.section_id)?.name_en)}
                </span>
              </div>
              <span className="text-sm font-semibold tabular-nums">{progress}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          <Card key={q.id} className="p-8 lg:p-10 shadow-elegant bg-card fade-in-up" data-testid="assessment-question-card">
            {assignment?.tp_context && (
              <div className="sticky top-0 z-20 mb-3 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm" data-testid="tp-subject-banner">
                <span className="font-semibold text-primary">
                  {i18n.language?.startsWith('es') ? 'Evaluando a' : 'Evaluating'}: {assignment.tp_context.subject_name}
                </span>
                <span className="text-muted-foreground">
                  {assignment.tp_context.subject_position ? ` · ${assignment.tp_context.subject_position}` : ''}
                  {assignment.tp_context.subject_department ? ` · ${assignment.tp_context.subject_department}` : ''}
                  {assignment.tp_context.cycle_name ? ` · ${assignment.tp_context.cycle_name}` : ''}
                  {' · '}
                  {i18n.language?.startsWith('es')
                    ? (assignment.tp_context.evaluator_role === 'supervisor' ? 'Rol: Supervisor/a' : 'Rol: Cliente Interno')
                    : (assignment.tp_context.evaluator_role === 'supervisor' ? 'Role: Supervisor' : 'Role: Internal Client')}
                </span>
              </div>
            )}
            {idx === 0 && questionnaire && (questionnaire.objective || questionnaire.instructions) && (
              <div className="mb-6 space-y-2" data-testid="assessment-intro-meta">
                {questionnaire.objective && (
                  <div className="rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-foreground/85 leading-relaxed">
                    <p className="text-xs uppercase tracking-wider font-semibold text-primary mb-1">
                      {t('library.metadata.objective.label', { defaultValue: 'Objective' })}
                    </p>
                    {questionnaire.objective}
                  </div>
                )}
                {questionnaire.instructions && (
                  <div className="rounded-xl border border-accent/20 bg-accent/5 px-4 py-3 text-sm text-foreground/85 leading-relaxed">
                    <p className="text-xs uppercase tracking-wider font-semibold text-accent mb-1">
                      {t('library.metadata.instructions.label', { defaultValue: 'Instructions' })}
                    </p>
                    {questionnaire.instructions}
                  </div>
                )}
              </div>
            )}
            {(() => {
              const sec = version?.sections?.find(s => s.id === q.section_id);
              const intro = tx(sec?.description, sec?.description_en);
              if (!intro) return null;
              return (
                <div
                  data-testid="section-intro"
                  className="mb-6 rounded-xl border border-accent/30 bg-accent/5 px-4 py-3 text-sm text-foreground/80 leading-relaxed"
                >
                  <p className="text-xs uppercase tracking-wider font-semibold text-accent mb-1">
                    {isEN ? 'How to answer' : 'Cómo responder'}
                  </p>
                  {intro}
                </div>
              );
            })()}
            <div className="flex items-start gap-3 mb-6">
              <div className="h-10 w-10 rounded-lg bg-gradient-primary text-white flex items-center justify-center font-bold text-sm">{idx + 1}</div>
              <div className="flex-1 min-w-0">
                <h2 className="font-display text-xl lg:text-2xl font-bold tracking-tight leading-snug text-balance">{tx(q.prompt, q.prompt_en)}</h2>
                {(q.dimension_tag === 'voluntary_hr_only' || q.dimension_tag === 'anonymous') && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid="question-tag-row">
                    {q.dimension_tag === 'voluntary_hr_only' && (
                      <span
                        data-testid="question-tag-hr-only"
                        className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-warning/10 text-warning border border-warning/20"
                      >
                        {t('pages.endUserAssessment.tagHrOnly')}
                      </span>
                    )}
                    {q.dimension_tag === 'anonymous' && (
                      <span
                        data-testid="question-tag-anonymous"
                        className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-accent/10 text-accent border border-accent/20"
                      >
                        {t('pages.endUserAssessment.tagAnonymous')}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {q.type === 'date' ? (
              <div className="max-w-xs" data-testid={`date-input-${q.id}`}>
                <LocalizedDateInput
                  id={`date-${q.id}`}
                  value={answers[q.id] || ''}
                  onChange={(v) => select(v)}
                  data-testid={`date-picker-${q.id}`}
                />
                {q.required === false && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {isEN ? 'Optional — you may leave it blank.' : 'Opcional — puedes dejarla en blanco.'}
                  </p>
                )}
              </div>
            ) : q.type === 'long_text' || q.type === 'text' ? (
              <Textarea
                data-testid={`textarea-${q.id}`}
                value={answers[q.id] || ''}
                onChange={(e) => select(e.target.value)}
                placeholder={isEN ? 'Type your answer here…' : 'Escribe tu respuesta aquí…'}
                rows={q.type === 'long_text' ? 6 : 3}
                className="text-sm leading-relaxed"
              />
            ) : q.type === 'multi_choice' ? (
              <div className="space-y-3" data-testid={`multi-choice-${q.id}`}>
                <p className="text-xs text-muted-foreground -mt-2">
                  {t('pages.endUserAssessment.multiHint', { defaultValue: 'Selecciona todas las opciones que apliquen' })}
                </p>
                {q.options.map((opt) => (
                  <Label
                    key={opt.id}
                    htmlFor={`${q.id}-${opt.id}`}
                    data-testid={`option-${opt.id}`}
                    className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-smooth hover:bg-muted/40 ${isSelected(opt.id) ? 'border-primary bg-primary/5 shadow-soft' : 'border-border'}`}
                  >
                    <Checkbox
                      id={`${q.id}-${opt.id}`}
                      checked={isSelected(opt.id)}
                      onCheckedChange={() => toggleMulti(opt.id)}
                      className="mt-1"
                    />
                    <span className="text-sm leading-relaxed font-medium">{tx(opt.label, opt.label_en)}</span>
                  </Label>
                ))}
              </div>
            ) : (
              <RadioGroup value={answers[q.id] || ''} onValueChange={select} className="space-y-3">
                {q.options.map((opt) => (
                  <Label
                    key={opt.id}
                    htmlFor={`${q.id}-${opt.id}`}
                    data-testid={`option-${opt.id}`}
                    className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-smooth hover:bg-muted/40 ${isSelected(opt.id) ? 'border-primary bg-primary/5 shadow-soft' : 'border-border'}`}
                  >
                    <RadioGroupItem value={opt.id} id={`${q.id}-${opt.id}`} className="mt-1" />
                    <span className="text-sm leading-relaxed font-medium">{tx(opt.label, opt.label_en)}</span>
                  </Label>
                ))}
              </RadioGroup>
            )}

            <div className="mt-8 pt-6 border-t border-border flex items-center justify-between gap-3">
              <Button data-testid="prev-question-button" variant="outline" onClick={prev} disabled={idx === 0} className="gap-1.5"><ArrowLeft className="h-4 w-4" /> {t('pages.endUserAssessment.previous')}</Button>
              <div className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5 text-accent" /> {t('pages.endUserAssessment.answerHonestly', { defaultValue: 'No right or wrong — answer honestly' })}
              </div>
              {idx < total - 1 ? (
                <Button data-testid="next-question-button" onClick={next} disabled={q.required !== false && !isAnswered(q.id)} className="bg-gradient-primary hover:opacity-90 gap-1.5">{t('pages.endUserAssessment.next')} <ArrowRight className="h-4 w-4" /></Button>
              ) : (
                <Button data-testid="submit-assessment-button" onClick={submit} disabled={(q.required !== false && !isAnswered(q.id)) || submitting} className="bg-gradient-primary hover:opacity-90 gap-1.5">
                  {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> {t('pages.endUserAssessment.submitting')}</> : <><CheckCircle2 className="h-4 w-4" /> {t('common.submit')}</>}
                </Button>
              )}
            </div>
          </Card>

          <div className="mt-6 flex flex-wrap gap-2 justify-center">
            {questions.map((qq, i) => {
              const answered = isAnswered(qq.id);
              const navLabel = answered
                ? t('pages.endUserAssessment.navAnswered', { defaultValue: 'Question {{n}} · answered', n: i + 1 })
                : t('pages.endUserAssessment.navPending', { defaultValue: 'Question {{n}} · pending', n: i + 1 });
              return (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  title={navLabel}
                  aria-label={navLabel}
                  data-testid={`assessment-nav-${i + 1}`}
                  className={`h-8 w-8 rounded-lg text-xs font-semibold transition-smooth ${
                    i === idx
                      ? 'bg-primary text-primary-foreground shadow-soft'
                      : answered
                      ? 'bg-success/15 text-success'
                      : 'bg-muted text-muted-foreground hover:bg-muted/70'
                  }`}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}
